import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ColumnDef } from '@tanstack/react-table';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import { DataTable } from 'components/DataTableComponent/DataTable';

interface IProduct {
  _id: string;
  image?: string;
  name: string;
  amount?: number | string;
  priority?: number | string;
  type?: string;
  productId?: string;
}

interface IProductsTableProps {
  products: IProduct[];
  onEdit: (product: IProduct) => void;
  onDelete: (product: IProduct) => void;
  onReorder?: (reorderedProducts: IProduct[]) => void;
}

export const ProductsTable: React.FC<IProductsTableProps> = ({
  products,
  onEdit,
  onDelete,
  onReorder
}) => {
  const [orderedProducts, setOrderedProducts] = useState<IProduct[]>(products);

  useEffect(() => {
    setOrderedProducts(products);
  }, [products]);

  const handleReorder = useCallback(
    (newOrder: IProduct[]): void => {
      setOrderedProducts(newOrder);
      onReorder?.(newOrder);
    },
    [onReorder]
  );

  const columns: ColumnDef<IProduct>[] = useMemo(
    () => [
      {
        accessorKey: 'image',
        header: 'Image',
        cell: ({ row }) => (
          <img
            src={row.original.image}
            alt="product"
            className="max-w-[48px] max-h-[48px] object-contain"
          />
        )
      },
      {
        accessorKey: 'name',
        header: 'Name'
      },
      {
        accessorKey: 'amount',
        header: 'Amount'
      },
      {
        accessorKey: 'priority',
        header: 'Priority'
      },
      {
        accessorKey: 'type',
        header: 'Type'
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => onEdit(row.original)
              },
              {
                label: 'Delete',
                handler: () => onDelete(row.original)
              }
            ]}
          />
        )
      }
    ],
    [onEdit, onDelete]
  );

  if (products.length === 0) {
    return null;
  }

  return (
    <div className="w-full mt-4">
      <DataTable
        data={orderedProducts}
        columns={columns}
        rowIdIdentifier="_id"
        showPagination={false}
        enableRowReorder
        onReorder={handleReorder}
        dragHandleColumnId="image"
        className="[&_td]:px-2 [&_th]:px-2 [&_td:first-child]:pl-0 [&_th:first-child]:pl-0"
      />
    </div>
  );
};
